'use client'

import { useState } from 'react'

interface WaitlistFormProps {
  eventId: string
  /** Optional specific ticket tier the customer is waiting on. */
  ticketTypeId?: string
  /** Heading copy override (e.g. when shown under a sold-out tier). */
  heading?: string
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Capture form shown on a sold-out event (or ticket tier). Collects the
 * customer's email + optional name and POSTs to the public /api/waitlist
 * endpoint. Gated by the caller on branding.publicSite.features.enableWaitlist.
 */
export default function WaitlistForm({ eventId, ticketTypeId, heading }: WaitlistFormProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmed = email.trim()
    if (!EMAIL_RE.test(trimmed)) {
      setError('Please enter a valid email address.')
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId,
          ticketTypeId,
          email: trimmed,
          name: name.trim() || undefined,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) {
        setError(data.error || 'Could not join the waitlist. Please try again.')
        return
      }
      setSuccess(true)
    } catch {
      setError('Could not join the waitlist. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="bg-success-light border border-success/20 rounded-xl p-5 text-center">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-success/10 mb-3">
          <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-text-primary font-semibold">You are on the waitlist</p>
        <p className="text-text-secondary text-sm mt-1">
          We will email you if tickets become available. Spots are limited and may go fast.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-surface border border-surface-border rounded-xl p-5 shadow-card">
      <h3 className="text-base font-semibold text-text-primary">{heading || 'Join the waitlist'}</h3>
      <p className="text-text-secondary text-sm mt-1 mb-4">
        Sold out for now. Leave your email and we will let you know if tickets open up.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <label htmlFor="waitlist-name" className="block text-sm font-medium text-text-secondary">
            Name <span className="text-text-tertiary font-normal">(optional)</span>
          </label>
          <input
            id="waitlist-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            placeholder="Your name"
            className="w-full px-4 py-2.5 bg-surface border border-surface-border rounded-btn text-text-primary text-base placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:border-primary focus:ring-primary/20 disabled:opacity-50 transition-colors"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="waitlist-email" className="block text-sm font-medium text-text-secondary">
            Email
          </label>
          <input
            id="waitlist-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 bg-surface border border-surface-border rounded-btn text-text-primary text-base placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:border-primary focus:ring-primary/20 disabled:opacity-50 transition-colors"
          />
        </div>
        {error && (
          <p className="text-sm text-error flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        )}
        <button type="submit" disabled={submitting} className="btn-primary w-full py-2.5 text-sm disabled:opacity-50">
          {submitting ? 'Joining...' : 'Notify Me'}
        </button>
      </form>
    </div>
  )
}
